import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TVSeriesCard from '@/components/feature/TVSeriesCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { tvSeries, categories } from '@/mocks/tvSeries';

type SortKey = 'default' | 'rating' | 'year' | 'episodes';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'default', label: '默认排序' },
  { key: 'rating', label: '评分最高' },
  { key: 'year', label: '最新上线' },
  { key: 'episodes', label: '集数最多' },
];

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<SortKey>('default');
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const filteredSeries = useMemo(() => {
    let result = activeCategory === 'all'
      ? [...tvSeries]
      : tvSeries.filter((s) => s.categorySlug === activeCategory);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'episodes':
        result.sort((a, b) => b.episodes - a.episodes);
        break;
      default:
        break;
    }
    return result;
  }, [activeCategory, sortBy]);

  const pageTitle = currentCategory
    ? `${currentCategory.name}电视剧 - 天美影视高清免费在线观看`
    : '天美影视电视剧大全 - 最新电视剧全集高清免费在线观看';
  const pageDesc = currentCategory
    ? `天美影视${currentCategory.name}电视剧大全，最新${currentCategory.name}全集高清免费在线观看，不缓冲流畅追剧。`
    : '天美影视电视剧大全，最新电视剧全集高清免费在线观看，不缓冲流畅追剧，免费看全集电视剧。';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`天美影视,${currentCategory ? currentCategory.name + '电视剧,' : ''}电视剧大全,最新电视剧,高清电视剧,全集在线看,免费在线观看`}
        canonicalUrl={`https://www.tianmeitv.com/category/${activeCategory}`}
      />

      <Navbar />

      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory ? currentCategory.name : '电视剧大全' },
              ]}
            />
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {currentCategory ? currentCategory.name : '电视剧大全'}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredSeries.length} 部剧集
              {currentCategory && ` · ${currentCategory.name}类电视剧高清免费在线观看`}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 pb-4 overflow-x-auto flex-wrap">
            <Link
              to="/category/all"
              onClick={() => setActiveCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2 mb-6">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  sortBy === opt.key
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Series Grid */}
          {filteredSeries.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredSeries.map((s) => (
                <TVSeriesCard key={s.id} series={s} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的剧集</p>
              <Link
                to="/category/all"
                className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors"
              >
                查看全部剧集
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}